import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, Trash, Buildings, Key, Package, CheckCircle } from "@phosphor-icons/react";

/**
 * ClientsAdminPanel
 * -----------------
 * Admin / manager-only tab inside Approvals. Lets an admin provision a
 * client user (external customer) linked to a Navitrade stockpile.
 */
export default function ClientsAdminPanel({ actorRole }) {
    const canDelete = actorRole === "admin";
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        name: "",
        username: "",
        password: "",
        email: "",
        client_name: "",
        stockpile_id: "A",
    });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/clients");
            setRows(data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.username.trim() || !form.password || !form.client_name.trim()) {
            toast.error("Name, username, password and client name are required");
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/clients", {
                name: form.name.trim(),
                username: form.username.trim(),
                password: form.password,
                email: form.email.trim() || null,
                client_name: form.client_name.trim(),
                stockpile_id: form.stockpile_id,
            });
            toast.success(`Client login created for ${form.client_name}`);
            setForm({ name: "", username: "", password: "", email: "", client_name: "", stockpile_id: "A" });
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const resetPassword = async (id, label) => {
        const pw = window.prompt(`Enter a new password for ${label}:`);
        if (!pw) return;
        if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        try {
            await api.patch(`/admin/clients/${id}`, { password: pw });
            toast.success("Password reset");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const changePile = async (id, pid) => {
        try {
            await api.patch(`/admin/clients/${id}`, { stockpile_id: pid });
            toast.success(`Linked to Stockpile ${pid}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id, label) => {
        if (!window.confirm(`Delete client login for ${label}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/clients/${id}`);
            toast.success("Client deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="admin-clients-panel">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="uppercase-label text-[var(--grindrod-red)]">Client access</div>
                    <h2 className="font-display font-black text-xl text-[var(--grindrod-navy)]">External client logins</h2>
                    <p className="text-slate-500 text-xs mt-0.5">
                        Create a login for a customer. They only see their allocated Navitrade stockpile and vessels matching their client name.
                    </p>
                </div>
                <button
                    onClick={() => setShowForm((s) => !s)}
                    className="btn-navy !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5"
                    data-testid="toggle-create-client"
                >
                    <UserPlus size={14} weight="bold" /> {showForm ? "Cancel" : "New client login"}
                </button>
            </div>

            {showForm && (
                <form onSubmit={submit} className="g-card p-4 mb-4 grid gap-3 sm:grid-cols-2" data-testid="create-client-form">
                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500">Contact name</label>
                        <input
                            className="g-input w-full"
                            placeholder="e.g. John Smith"
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            data-testid="client-field-name"
                            required
                        />
                    </div>
                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500">Company / Client name</label>
                        <input
                            className="g-input w-full"
                            placeholder="e.g. Exaro, Canyon Coal, Tata Steel"
                            value={form.client_name}
                            onChange={(e) => update("client_name", e.target.value)}
                            data-testid="client-field-client-name"
                            required
                        />
                    </div>
                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500">Username (login ID)</label>
                        <input
                            className="g-input w-full tabular"
                            placeholder="e.g. exaro.client"
                            value={form.username}
                            onChange={(e) => update("username", e.target.value)}
                            data-testid="client-field-username"
                            required
                        />
                    </div>
                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500">Temporary password</label>
                        <input
                            type="text"
                            className="g-input w-full tabular"
                            placeholder="at least 6 characters"
                            value={form.password}
                            onChange={(e) => update("password", e.target.value)}
                            data-testid="client-field-password"
                            required
                            minLength={6}
                        />
                    </div>
                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500">Email (optional)</label>
                        <input
                            type="email"
                            className="g-input w-full"
                            placeholder="contact@client.com"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            data-testid="client-field-email"
                        />
                    </div>
                    <div>
                        <label className="uppercase-label text-[10px] text-slate-500">Allocated stockpile</label>
                        <select
                            className="g-input w-full"
                            value={form.stockpile_id}
                            onChange={(e) => update("stockpile_id", e.target.value)}
                            data-testid="client-field-stockpile"
                        >
                            {["A", "B", "C", "D"].map((p) => (
                                <option key={p} value={p}>Stockpile {p}</option>
                            ))}
                        </select>
                    </div>
                    <div className="sm:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={saving}
                            className="btn-primary text-xs inline-flex items-center gap-1.5"
                            data-testid="submit-client-form"
                        >
                            <CheckCircle size={14} weight="bold" />
                            {saving ? "Creating…" : "Create client login"}
                        </button>
                    </div>
                </form>
            )}

            <div className="g-card overflow-x-auto">
                <table className="g-table" data-testid="clients-table">
                    <thead>
                        <tr>
                            <th>Contact</th>
                            <th>Client / Company</th>
                            <th>Username</th>
                            <th>Stockpile</th>
                            <th>Email</th>
                            <th>Created</th>
                            <th className="text-right pr-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center text-slate-400 py-8">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={7} className="text-center text-slate-400 py-8" data-testid="clients-empty">
                                No client logins yet. Click <b>New client login</b> to create one.
                            </td></tr>
                        ) : rows.map((c) => (
                            <tr key={c.id} data-testid={`client-row-${c.id}`}>
                                <td className="font-semibold text-[var(--grindrod-navy)]">{c.name}</td>
                                <td className="inline-flex items-center gap-1.5">
                                    <Buildings size={13} className="text-slate-400" /> {c.client_name}
                                </td>
                                <td className="tabular">{c.username}</td>
                                <td>
                                    <select
                                        className="g-input !py-1 !px-2 text-xs w-auto"
                                        value={c.stockpile_id || "A"}
                                        onChange={(e) => changePile(c.id, e.target.value)}
                                        data-testid={`client-pile-select-${c.id}`}
                                    >
                                        {["A", "B", "C", "D"].map((p) => <option key={p} value={p}>Stockpile {p}</option>)}
                                    </select>
                                </td>
                                <td className="text-xs text-slate-500">{c.email || "—"}</td>
                                <td className="text-xs text-slate-500">
                                    {c.created_at ? new Date(c.created_at).toLocaleString(undefined, { hour12: false }) : "—"}
                                </td>
                                <td className="text-right pr-4">
                                    <div className="inline-flex gap-2 flex-wrap justify-end">
                                        <button
                                            onClick={() => resetPassword(c.id, c.client_name)}
                                            className="btn-outline !py-1 !px-2 text-xs inline-flex items-center gap-1"
                                            data-testid={`client-reset-pw-${c.id}`}
                                        >
                                            <Key size={11} /> Reset password
                                        </button>
                                        {canDelete && (
                                            <button
                                                onClick={() => remove(c.id, c.client_name)}
                                                className="btn-primary !py-1 !px-2 text-xs inline-flex items-center gap-1"
                                                data-testid={`client-delete-${c.id}`}
                                            >
                                                <Trash size={11} /> Delete
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
